import React, { useCallback, useEffect, useState } from 'react';
import observabilidadeService from '../../services/observabilidadeService';

const STATUS_FINAL_SUCESSO = 22;
const MINUTOS_PARADO = 15;
const MS_POR_MINUTO = 60_000;

function classificarItem(item, agora) {
  const { indirectMonitoringProcessmentStatusId: statusId, indirectMonitoringProcessmentStatusLastDateTime: lastDt } = item;

  if (statusId === null) return null;

  if (statusId > 1000) return 'erro';

  if (
    statusId !== STATUS_FINAL_SUCESSO &&
    lastDt !== null &&
    agora - new Date(lastDt) > MINUTOS_PARADO * MS_POR_MINUTO
  ) {
    return 'parado';
  }

  return null;
}

function formatarDuracao(ms) {
  const totalMin = Math.floor(ms / MS_POR_MINUTO);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatarDataBR(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function PainelUltimos3Dias() {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [companyMap, setCompanyMap] = useState(new Map());
  const [usinaFiltro, setUsinaFiltro] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const [monitoramentos, statusMap] = await Promise.all([
        observabilidadeService.getMonitoramentosUltimosDias(5),
        observabilidadeService.getAllProcessmentStatuses(),
      ]);

      const agora = new Date();
      const classificados = [];

      for (const item of monitoramentos) {
        const tipo = classificarItem(item, agora);
        if (!tipo) continue;

        const statusInfo = statusMap.get(item.indirectMonitoringProcessmentStatusId) ?? null;
        const lastDt = item.indirectMonitoringProcessmentStatusLastDateTime;
        const tempoMs = lastDt ? agora - new Date(lastDt) : 0;

        classificados.push({ ...item, tipo, statusInfo, tempoMs });
      }

      // Erros primeiro, depois parados; ambos por tempo decrescente
      classificados.sort((a, b) => {
        if (a.tipo !== b.tipo) return a.tipo === 'erro' ? -1 : 1;
        return b.tempoMs - a.tempoMs;
      });

      // Buscar nomes das usinas
      const uniqueIds = [...new Set(classificados.map(i => i.companyId))];
      const results = await Promise.allSettled(
        uniqueIds.map(id => observabilidadeService.getCompanyById(id))
      );
      const novoMap = new Map();
      uniqueIds.forEach((id, idx) => {
        const r = results[idx];
        if (r.status === 'fulfilled') novoMap.set(id, r.value.description);
      });
      setCompanyMap(novoMap);
      setUsinaFiltro('');

      setItens(classificados);
      setUltimaAtualizacao(new Date());
    } catch (e) {
      setErro(e.message || 'Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const itensFiltrados = usinaFiltro
    ? itens.filter(item => String(item.companyId) === usinaFiltro)
    : itens;

  const tituloContador = loading ? '' : ` (${itensFiltrados.length} ${itensFiltrados.length === 1 ? 'item' : 'itens'})`;

  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h5 className="mb-0">
          Visão Últimos 5 Dias
          {!loading && <span className="text-muted fs-6 fw-normal">{tituloContador}</span>}
        </h5>
        <div className="d-flex align-items-center gap-3">
          {/* Filtro de Usina */}
          {companyMap.size > 0 && (
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', minWidth: '160px' }}
              value={usinaFiltro}
              onChange={e => setUsinaFiltro(e.target.value)}
              disabled={loading}
            >
              <option value="">Todas as Usinas</option>
              {[...companyMap.entries()].map(([id, desc]) => (
                <option key={id} value={String(id)}>{desc}</option>
              ))}
            </select>
          )}
          {ultimaAtualizacao && !loading && (
            <small className="text-muted">
              Atualizado às {ultimaAtualizacao.toLocaleTimeString('pt-BR')}
            </small>
          )}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={carregar}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                Carregando…
              </>
            ) : (
              'Atualizar'
            )}
          </button>
        </div>
      </div>

      <div className="card-body p-0">
        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-secondary" role="status">
              <span className="visually-hidden">Carregando…</span>
            </div>
          </div>
        )}

        {!loading && erro && (
          <div className="p-4">
            <div className="alert alert-danger mb-3">{erro}</div>
            <button className="btn btn-sm btn-danger" onClick={carregar}>
              Tentar novamente
            </button>
          </div>
        )}

        {!loading && !erro && itensFiltrados.length === 0 && (
          <div className="p-4 text-muted text-center">
            {itens.length === 0
              ? 'Nenhum monitoramento requer atenção no período.'
              : 'Nenhum monitoramento encontrado para a usina selecionada.'}
          </div>
        )}

        {!loading && !erro && itensFiltrados.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Usina</th>
                  <th>Tipo</th>
                  <th>Status ID</th>
                  <th>Descrição do Status</th>
                  <th>Aplicação</th>
                  <th>Tempo no Status</th>
                  <th>Data Coleta</th>
                  <th>E-mail</th>
                  <th>ID Armadilha</th>
                </tr>
              </thead>
              <tbody>
                {itensFiltrados.map(item => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{companyMap.get(item.companyId) ?? `ID ${item.companyId}`}</td>
                    <td>
                      {item.tipo === 'erro' ? (
                        <span className="badge bg-danger">🔴 Erro</span>
                      ) : (
                        <span className="badge bg-warning text-dark">🟡 Parado</span>
                      )}
                    </td>
                    <td>{item.indirectMonitoringProcessmentStatusId}</td>
                    <td>{item.statusInfo?.description ?? '—'}</td>
                    <td>{item.statusInfo?.application ?? '—'}</td>
                    <td>{item.tempoMs ? formatarDuracao(item.tempoMs) : '—'}</td>
                    <td>{formatarDataBR(item.collectDateTime)}</td>
                    <td>{item.userEmail}</td>
                    <td>{item.flyTrapId ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PainelUltimos3Dias;
